import { createStateHook } from '@atlassian/react-sweet-state';
import type { Actions } from './actions.tsx';
import { ArchiveIssuesStore } from './main.tsx';
import type { State, PageNumber, IssueId, SelectedIssueDetailsType } from './types.tsx';

export const useArchiveIssueStates = createStateHook<State, Actions>(ArchiveIssuesStore);

export const getSelectedIssuesForPage = ({
	state,
	pageNumber,
}: {
	state: State;
	pageNumber: PageNumber;
}) => {
	const selectedIssues = state.selectedIssues?.[pageNumber];
	return selectedIssues || [];
};

export const getAllSelectedIssues = ({ state }: { state: State }) => {
	if (!state.selectedIssues) {
		return [];
	}
	const selectedIssues = Object.values(state.selectedIssues);
	return selectedIssues.flat();
};

export const getAllSelectedIssuesWithDetails = ({
	state,
}: {
	state: State;
}): SelectedIssueDetailsType[] => {
	if (!state.selectedIssues) {
		return [];
	}
	const issueDetails = state.issueDetails;

	const selectedIssues = Object.values(state.selectedIssues);
	return selectedIssues.flat().map((issueId) => {
		return issueDetails[issueId];
	});
};

export const getIssueKeyForSelectedIssue = ({
	state,
	issueId,
}: {
	state: State;
	issueId: IssueId;
}) => {
	let selectedIssuePagenumber;
	for (const pageNumber in state.selectedIssues) {
		if (state.selectedIssues[pageNumber].includes(issueId)) {
			selectedIssuePagenumber = Number(pageNumber);
		}
	}
	if (selectedIssuePagenumber === undefined) {
		return undefined;
	}
	return state.issuesMockData[selectedIssuePagenumber].find(
		(issue) => issue.issueId.toString() === issueId,
	)?.issueKey;
};

export const isIssueSelected = ({
	state,
	pageNumber,
	issueId,
}: {
	issueId: IssueId;
	state: State;
	pageNumber: PageNumber;
}) => {
	const selectedIssues = state.selectedIssues[pageNumber];
	return !!selectedIssues && selectedIssues.includes(issueId);
};

export const getNumberOfSelectedIssues = ({ state }: { state: State }) =>
	state.numberOfSelectedIssues;

export const getNumberOfSelectedIssuesForPage = ({
	state,
	pageNumber,
}: {
	state: State;
	pageNumber: PageNumber;
}) => {
	const selectedIssues = state.selectedIssues[pageNumber];
	return selectedIssues ? selectedIssues.length : 0;
};

export const getIssueIdsForCurrentPage = ({ state }: { state: State }) => state.issuesInCurrentPage;

export const getNumberOfIssuesForCurrentPage = ({ state }: { state: State }) =>
	state.issuesInCurrentPage.length;

export const getLoadingState = ({ state }: { state: State }) => state.isLoading;

export const getCurrentPage = ({ state }: { state: State }) => state.currentPage;

export const getTotalPages = ({ state }: { state: State }) => state.totalPages;

export const getProjectId = ({ state }: { state: State }) => state.projectId;

export const getSelectedFilters = ({ state }: { state: State }) => state.filters;

export const getIssuesForCurrentPage = ({
	state,
	currentPage,
}: {
	state: State;
	currentPage: number;
}) => state.issuesMockData[currentPage] || [];
